/**
 * Schichten — der Schichtplan als Instanz-Kalender (Phase B).
 *
 * „Der Kalender IST die Instanzliste" (SCHICHTINSTANZEN-VORSCHLAG.md): Jede
 * Zelle zeigt die materialisierten Schichten des Tages; die Zukunft entsteht
 * beim Laden über den Generator. Der eigentliche Zweck ist das Frühwarnsystem:
 * **Ein künftiger Werktag oder Samstag ohne Schicht ist als Lücke sichtbar,
 * bevor er wehtut** — der Samstagsregel-Fall (läuft am 05.09. aus).
 *
 * Die klassische Schichtregel-Pflege bleibt unter Einrichten → Schichtregeln.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Kennzahl, Notiz } from '../components/leitstand';

interface Instanz {
  id: string;
  datum: string;
  shiftTypeCode: string;
  status: 'GEPLANT' | 'LAUFEND' | 'GESCHLOSSEN' | 'AUSGEFALLEN';
  startTime: string;
  endTime: string;
  herkunft: 'KALENDER' | 'IMPORT';
  bearbeitet: boolean;
  farbe: string | null;
}

interface Schichtart {
  code: string;
  label?: string;
  name?: string;
  color?: string | null;
}

interface Vorlage {
  id: string;
  name: string;
  hinweis: string;
  belegTag: string | null;
  giltFuerWochentage: number[];
  arten: Array<{ shiftTypeCode: string; startTime: string; endTime: string }>;
}

/** Was im Planungsfenster je Schichtart eingestellt ist. */
interface ArtWahl {
  an: boolean;
  von: string;
  bis: string;
}

interface Bilanz {
  angelegt: number;
  geaendert: number;
  uebersprungen: Array<{ tag: string; art: string; grund: string }>;
  luecken: Array<{ von: string; nach: string; ab: string; bis: string }>;
}

type Auswahl = { art: 'bearbeiten'; instanz: Instanz } | { art: 'planen'; tage: string[] };

const SCHICHT_LABEL: Record<string, string> = {
  EARLY: 'FRÜH',
  LATE: 'SPÄT',
  SATURDAY: 'SAMSTAG',
  NIGHT: 'NACHT',
};

/** Lesbarer Text auf frei gewählter Farbe (YIQ-Helligkeit). */
function kontrastZu(hex: string): string {
  const n = hex.replace('#', '');
  if (n.length !== 6) return '#fff';
  const r = parseInt(n.slice(0, 2), 16),
    g = parseInt(n.slice(2, 4), 16),
    b = parseInt(n.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#08120F' : '#ffffff';
}

function SchichtChip({ code, farbe }: { code: string; farbe: string | null }) {
  const label = SCHICHT_LABEL[code] ?? code;
  if (!farbe) return <Kennung>{label}</Kennung>;
  return (
    <span
      className="rounded-[4px] px-[7px] py-[3px] font-zahl text-[10px] tracking-[0.08em]"
      style={{ backgroundColor: farbe, color: kontrastZu(farbe) }}
    >
      {label}
    </span>
  );
}

function tagPlus(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function montagVon(iso: string): string {
  const wochentag = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return tagPlus(iso, -((wochentag + 6) % 7));
}

function kw(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const jahresanfang = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - jahresanfang.getTime()) / 86400000 + 1) / 7);
}

export default function ShiftsLeitstand() {
  const { user } = useAuth();

  const heute = getTodayBerlin(new Date());
  const von = montagVon(tagPlus(heute, -14));
  const bis = tagPlus(montagVon(heute), 7 * 8 - 1); // 8 Wochen ab aktueller Woche

  const [instanzen, setInstanzen] = useState<Instanz[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const [schichtarten, setSchichtarten] = useState<Schichtart[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const [formStart, setFormStart] = useState('');
  const [formEnde, setFormEnde] = useState('');
  const [sende, setSende] = useState(false);
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [bilanz, setBilanz] = useState<Bilanz | null>(null);

  // Was im Planungsfenster je Schichtart eingestellt ist.
  const [gewaehlt, setGewaehlt] = useState<Record<string, ArtWahl>>({});

  // Ziehen über mehrere Tage: gemerkt werden Anfang und aktuelles Ende als
  // (Woche, Spalte) — daraus entsteht ein Rechteck, wie im Tabellenkalkulator.
  const [ziehen, setZiehen] = useState<{
    vonW: number;
    vonS: number;
    bisW: number;
    bisS: number;
  } | null>(null);

  // Escape schliesst das Overlay — erwartet man von jedem Fenster, das sich
  // ueber den Inhalt legt.
  useEffect(() => {
    if (!auswahl) return;
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sende) setAuswahl(null);
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [auswahl, sende]);

  useEffect(() => {
    fetch('/api/shifts/types', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSchichtarten(Array.isArray(d) ? d : []))
      .catch(() => setSchichtarten([]));
    // Die Vorlagen pflegt niemand — sie zeigen, was zuletzt für so einen Tag
    // geplant wurde.
    fetch('/api/shifts/vorlagen', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setVorlagen(Array.isArray(d) ? d : []))
      .catch(() => setVorlagen([]));
  }, []);

  const jetztBerlin = () =>
    new Date().toLocaleTimeString('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
    });

  const laeuft = (i: Instanz) =>
    i.datum === heute && i.startTime <= jetztBerlin() && jetztBerlin() < i.endTime;

  const istVorbei = (i: Instanz) => {
    if (i.datum < heute) return true;
    if (i.datum > heute) return false;
    return i.endTime <= jetztBerlin();
  };

  const waehleBearbeiten = (i: Instanz) => {
    if (i.status === 'GESCHLOSSEN' || istVorbei(i)) return;
    setAuswahl({ art: 'bearbeiten', instanz: i });
    setFormStart(i.startTime);
    setFormEnde(i.endTime);
    setAktionsFehler('');
  };

  /** Wochentag eines ISO-Tages, 1 = Montag … 7 = Sonntag. */
  const wochentagVon = (tag: string) => {
    const d = new Date(`${tag}T12:00:00Z`).getUTCDay();
    return d === 0 ? 7 : d;
  };

  /**
   * Die Vorlage, die zu den markierten Tagen passt: Samstage bekommen die
   * Samstagsvorlage, Werktage die Werktagsvorlage. Passt keine auf ALLE Tage,
   * gilt die erste — sie ist die häufigste Lage.
   */
  const passendeVorlage = (tage: string[]) =>
    vorlagen.find((v) => tage.every((t) => v.giltFuerWochentage.includes(wochentagVon(t)))) ??
    vorlagen[0];

  const nimmVorlage = (v: Vorlage) => {
    const neu: Record<string, ArtWahl> = {};
    for (const t of schichtarten) {
      const drin = v.arten.find((a) => a.shiftTypeCode === t.code);
      neu[t.code] = drin
        ? { an: true, von: drin.startTime, bis: drin.endTime }
        : { an: false, von: '04:00', bis: '14:45' };
    }
    setGewaehlt(neu);
  };

  /**
   * Eine Schichtzeit ändern — und die Nachbarschicht mitziehen.
   *
   * Die Grenze zwischen zwei Schichten ist EINE Grenze: Verschiebt man das Ende
   * der Frühschicht, beginnt die Spätschicht dort. Sonst entstünde eine
   * Überlappung, in der eine Erfassung zu beiden Schichten gehörte. Der Server
   * setzt dieselbe Regel durch; hier sieht man sie sofort.
   */
  const setzeZeit = (code: string, feld: 'von' | 'bis', wert: string) => {
    setGewaehlt((vorher) => {
      const neu = { ...vorher, [code]: { ...vorher[code], [feld]: wert } };
      // Reihenfolge VOR der Änderung — der neue Wert sortiert sonst um.
      const folge = Object.entries(vorher)
        .filter(([, g]) => g.an)
        .sort((a, b) => a[1].von.localeCompare(b[1].von))
        .map(([c]) => c);
      const i = folge.indexOf(code);
      if (feld === 'bis' && i >= 0 && i < folge.length - 1) {
        const nachbar = folge[i + 1];
        neu[nachbar] = { ...neu[nachbar], von: wert };
      }
      if (feld === 'von' && i > 0) {
        const nachbar = folge[i - 1];
        neu[nachbar] = { ...neu[nachbar], bis: wert };
      }
      return neu;
    });
  };

  const oeffnePlanen = (tage: string[]) => {
    if (!tage.length) return;
    setAuswahl({ art: 'planen', tage });
    setAktionsFehler('');
    setBilanz(null);
    const v = passendeVorlage(tage);
    if (v) nimmVorlage(v);
    else {
      const leer: Record<string, ArtWahl> = {};
      for (const t of schichtarten) leer[t.code] = { an: false, von: '04:00', bis: '14:45' };
      setGewaehlt(leer);
    }
  };

  const jeTag = useMemo(() => {
    const map = new Map<string, Instanz[]>();
    for (const i of instanzen) {
      const liste = map.get(i.datum) ?? [];
      liste.push(i);
      map.set(i.datum, liste);
    }
    return map;
  }, [instanzen]);

  // ── Was im Planungsfenster gerade eingestellt ist ───────────────────────
  const aktiveArten = useMemo(
    () =>
      Object.entries(gewaehlt)
        .filter(([, g]) => g.an)
        .map(([code, g]) => ({ shiftTypeCode: code, startTime: g.von, endTime: g.bis }))
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [gewaehlt],
  );

  /** Welche Vorlage beschreibt die eingestellten Zeiten gerade genau? */
  const aktiveVorlage = useMemo(
    () =>
      vorlagen.find(
        (v) =>
          v.arten.length === aktiveArten.length &&
          v.arten.every((a) =>
            aktiveArten.some(
              (b) =>
                b.shiftTypeCode === a.shiftTypeCode &&
                b.startTime === a.startTime &&
                b.endTime === a.endTime,
            ),
          ),
      ),
    [vorlagen, aktiveArten],
  );

  /**
   * Schließen die gewählten Schichten aneinander an? Dieselbe Prüfung wie im
   * Server (pruefeAnschluesse) — hier, damit man es sieht, bevor man speichert.
   */
  const anschluss = useMemo(() => {
    const ueberlappungen: Array<{ von: string; nach: string; ab: string; bis: string }> = [];
    const luecken: Array<{ von: string; nach: string; ab: string; bis: string }> = [];
    for (let i = 0; i < aktiveArten.length - 1; i++) {
      const a = aktiveArten[i],
        b = aktiveArten[i + 1];
      if (a.endTime < a.startTime) continue; // über Mitternacht: kein Nachbar am selben Tag
      if (a.endTime === b.startTime) continue;
      const eintrag = {
        von: a.shiftTypeCode,
        nach: b.shiftTypeCode,
        ab: a.endTime,
        bis: b.startTime,
      };
      if (a.endTime > b.startTime) ueberlappungen.push(eintrag);
      else luecken.push(eintrag);
    }
    return { ueberlappungen, luecken };
  }, [aktiveArten]);

  /** Wie viele der markierten Tage haben diese Schichten schon? */
  const bestehende = useMemo(() => {
    if (auswahl?.art !== 'planen') return 0;
    return auswahl.tage.reduce(
      (summe, tag) =>
        summe +
        aktiveArten.filter((a) =>
          (jeTag.get(tag) ?? []).some((i) => i.shiftTypeCode === a.shiftTypeCode),
        ).length,
      0,
    );
  }, [auswahl, aktiveArten, jeTag]);

  const knopfText = useMemo(() => {
    if (auswahl?.art !== 'planen') return 'Planen';
    const gesamt = auswahl.tage.length * aktiveArten.length;
    const neue = gesamt - bestehende;
    if (!gesamt) return 'Keine Schicht gewählt';
    if (!neue)
      return bestehende === 1 ? 'Zeiten ändern' : `Zeiten an ${bestehende} Schichten ändern`;
    if (!bestehende) return neue === 1 ? 'Schicht anlegen' : `${neue} Schichten anlegen`;
    return `${neue} anlegen, ${bestehende} ändern`;
  }, [auswahl, aktiveArten, bestehende]);

  /** Die markierten Tage in Worten — „Mo, Di, Mi — eine Woche". */
  const tageAlsText = (tage: string[]) => {
    const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const namen = [...new Set(tage.map((t) => WT[new Date(`${t}T12:00:00Z`).getUTCDay()]))];
    const spanne =
      tage.length > 1
        ? ` — ${tage[0].slice(8, 10)}.${tage[0].slice(5, 7)}. bis ` +
          `${tage[tage.length - 1].slice(8, 10)}.${tage[tage.length - 1].slice(5, 7)}.`
        : '';
    return namen.join(', ') + spanne;
  };

  /** Planen: mehrere Tage, mehrere Arten, ein Zug. */
  const plane = async () => {
    if (auswahl?.art !== 'planen') return;
    setSende(true);
    setAktionsFehler('');
    try {
      const res = await fetch('/api/shifts/instanzen/mehrere', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tage: auswahl.tage, arten: aktiveArten }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || 'Die Planung konnte nicht gespeichert werden');
      setBilanz(body as Bilanz);
      await lade();
      // Ohne Hinweise gleich schließen — sonst bleibt die Bilanz stehen.
      if (!(body as Bilanz).uebersprungen?.length) setAuswahl(null);
    } catch (e) {
      setAktionsFehler(
        e instanceof Error ? e.message : 'Die Planung konnte nicht gespeichert werden',
      );
    } finally {
      setSende(false);
    }
  };

  // ── Woche übernehmen ────────────────────────────────────────────────────
  /**
   * Die letzte geplante Woche vor dieser — aber nur, wenn diese Woche selbst
   * leer ist und in der Zukunft liegt. Sonst gäbe es das Angebot dort, wo schon
   * geplant wurde.
   */
  const uebernahmeQuelle = (tage: string[]): string[] | null => {
    if (tage[6] < heute) return null; // ganz vorbei
    if (tage.some((t) => (jeTag.get(t) ?? []).length > 0)) return null; // schon geplant
    const start = montagVon(tagPlus(heute, -14));
    for (let zurueck = 1; zurueck <= 8; zurueck++) {
      const quelle: string[] = [];
      for (let t = 0; t < 7; t++) quelle.push(tagPlus(tage[0], -7 * zurueck + t));
      if (quelle[0] < start) break;
      if (quelle.some((t) => (jeTag.get(t) ?? []).length > 0)) return quelle;
    }
    return null;
  };

  /**
   * Überträgt eine Woche auf eine andere — Wochentag auf Wochentag, damit der
   * Samstag seine Samstagszeiten behält. Jeder Tag geht einzeln, weil die Tage
   * verschiedene Schichten tragen können.
   */
  const uebernimmWoche = async (ziel: string[], quelle: string[]) => {
    setSende(true);
    setAktionsFehler('');
    try {
      for (let t = 0; t < 7; t++) {
        const vorbild = jeTag.get(quelle[t]) ?? [];
        if (!vorbild.length || ziel[t] < heute) continue;
        const res = await fetch('/api/shifts/instanzen/mehrere', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            tage: [ziel[t]],
            arten: vorbild
              .filter((i) => i.status !== 'AUSGEFALLEN')
              .map((i) => ({
                shiftTypeCode: i.shiftTypeCode,
                startTime: i.startTime,
                endTime: i.endTime,
              })),
          }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => null);
          throw new Error(b?.error || 'Die Woche konnte nicht übernommen werden');
        }
      }
      await lade();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Woche konnte nicht übernommen werden');
    } finally {
      setSende(false);
    }
  };

  // ── Ziehen über mehrere Tage ────────────────────────────────────────────
  // Der Zug spannt ein Rechteck auf: von der angefassten Zelle bis zur Zelle
  // unter dem Zeiger, über Wochengrenzen hinweg.
  const imZug = (w: number, sp: number) => {
    if (!ziehen) return false;
    const [w1, w2] = [Math.min(ziehen.vonW, ziehen.bisW), Math.max(ziehen.vonW, ziehen.bisW)];
    const [s1, s2] = [Math.min(ziehen.vonS, ziehen.bisS), Math.max(ziehen.vonS, ziehen.bisS)];
    return w >= w1 && w <= w2 && sp >= s1 && sp <= s2;
  };

  const gezogeneTage = useCallback(() => {
    if (!ziehen) return [];
    const [w1, w2] = [Math.min(ziehen.vonW, ziehen.bisW), Math.max(ziehen.vonW, ziehen.bisW)];
    const [s1, s2] = [Math.min(ziehen.vonS, ziehen.bisS), Math.max(ziehen.vonS, ziehen.bisS)];
    const start = montagVon(tagPlus(heute, -14));
    const tage: string[] = [];
    for (let w = w1; w <= w2; w++) {
      for (let sp = s1; sp <= s2; sp++) {
        const tag = tagPlus(start, w * 7 + sp);
        if (tag >= heute) tage.push(tag); // nichts rückwirkend planen
      }
    }
    return tage;
  }, [ziehen, heute]);

  // Loslassen irgendwo im Fenster beendet den Zug — sonst bleibt er hängen,
  // wenn die Maus das Raster verlässt.
  useEffect(() => {
    if (!ziehen) return;
    const beiLoslassen = () => {
      const tage = gezogeneTage();
      setZiehen(null);
      if (tage.length) oeffnePlanen(tage);
    };
    window.addEventListener('mouseup', beiLoslassen);
    return () => window.removeEventListener('mouseup', beiLoslassen);
  }, [ziehen, gezogeneTage]); // eslint-disable-line react-hooks/exhaustive-deps

  const aktion = async (pfad: string, methode: string, body?: object) => {
    setSende(true);
    setAktionsFehler('');
    try {
      const res = await fetch(pfad, {
        method: methode,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.error || 'Die Änderung konnte nicht gespeichert werden');
      }
      setAuswahl(null);
      await lade();
    } catch (e) {
      setAktionsFehler(
        e instanceof Error ? e.message : 'Die Änderung konnte nicht gespeichert werden',
      );
    } finally {
      setSende(false);
    }
  };

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(`/api/shifts/instanzen?from=${von}&to=${bis}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Der Schichtplan konnte nicht geladen werden');
      const body = await res.json();
      setInstanzen(Array.isArray(body) ? body : []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Schichtplan konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [von, bis]);

  useEffect(() => {
    void lade();
  }, [lade]);

  /**
   * Wie weit reicht der Plan? Seit im Kalender geplant wird statt Regeln zu
   * pflegen, ist das die einzige Frage — nicht mehr, welche Regel wann
   * ausläuft.
   */
  const horizont = useMemo(() => {
    const kuenftigeTage = instanzen
      .filter((i) => i.datum >= heute && i.status !== 'AUSGEFALLEN')
      .map((i) => i.datum)
      .sort();
    const letzterTag = kuenftigeTage[kuenftigeTage.length - 1] ?? null;
    const tageVoraus = letzterTag
      ? Math.round(
          (new Date(`${letzterTag}T00:00:00Z`).getTime() -
            new Date(`${heute}T00:00:00Z`).getTime()) /
            86_400_000,
        )
      : 0;
    return { letzterTag, tageVoraus, knapp: tageVoraus < 21 };
  }, [instanzen, heute]);

  const wochen = useMemo(() => {
    const start = montagVon(tagPlus(heute, -14));
    const liste: string[][] = [];
    for (let w = 0; w < 10; w++) {
      const tage: string[] = [];
      for (let t = 0; t < 7; t++) tage.push(tagPlus(start, w * 7 + t));
      liste.push(tage);
    }
    return liste;
  }, [heute]);

  const kuenftige = instanzen.filter((i) => i.datum >= heute).length;

  return (
    <LeitstandRahmen titel="Schichten" benutzer={user?.displayName}>
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && instanzen.length === 0 && !fehler && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Schichtplan …</p>
      )}

      {!laden && !fehler && (
        <div className="flex flex-col gap-6">
          {horizont.knapp && (
            <Notiz titel="Der Plan wird knapp">
              {horizont.letzterTag ? (
                <>
                  Geplant ist bis <b>{horizont.letzterTag.split('-').reverse().join('.')}</b> —
                  danach steht nichts. Über die Tage ziehen und planen, oder eine leere Woche per
                  „wie KW nn" übernehmen.
                </>
              ) : (
                <>
                  Für die kommenden Tage ist <b>keine Schicht geplant</b>. Über die Tage ziehen, um
                  zu planen.
                </>
              )}
            </Notiz>
          )}

          {/* Das Formular liegt als Overlay über dem Plan statt oben auf der
              Seite: Wer einen Tag anklickt, soll dort bleiben und nicht erst
              hochscrollen müssen (Andreas, 19.08.2026). */}
          {auswahl && (
            <div
              className="fixed inset-0 z-40 flex items-center justify-center bg-grund/80 p-4 backdrop-blur-[2px]"
              onClick={() => !sende && setAuswahl(null)}
              role="presentation"
            >
              <div
                className="w-full max-w-lg"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                {auswahl.art === 'planen' ? (
                  <Karte
                    titel="Schichten planen"
                    marke={
                      auswahl.tage.length === 1
                        ? auswahl.tage[0].split('-').reverse().join('.')
                        : `${auswahl.tage[0].slice(8, 10)}.${auswahl.tage[0].slice(5, 7)}. – ` +
                          `${auswahl.tage[auswahl.tage.length - 1].slice(8, 10)}.` +
                          `${auswahl.tage[auswahl.tage.length - 1].slice(5, 7)}.`
                    }
                  >
                    <div className="flex flex-col gap-3">
                      <div className="rounded-[4px] border border-akzent bg-akzent-tief px-3 py-2">
                        <div className="text-sm font-medium text-tinte">
                          {auswahl.tage.length === 1
                            ? 'An einem Tag'
                            : `An ${auswahl.tage.length} Tagen`}
                        </div>
                        <div className="text-[12px] text-tinte-2">{tageAlsText(auswahl.tage)}</div>
                      </div>

                      {vorlagen.length > 0 && (
                        <>
                          <label className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                            Vorlage
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {vorlagen.map((v) => (
                              <button
                                key={v.id}
                                onClick={() => nimmVorlage(v)}
                                aria-pressed={aktiveVorlage?.id === v.id}
                                className={`flex flex-col items-start gap-px rounded-[4px] border px-[11px] py-1.5 text-left ${
                                  aktiveVorlage?.id === v.id
                                    ? 'border-akzent bg-akzent text-akzent-satz'
                                    : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-akzent'
                                }`}
                              >
                                <span className="font-zahl text-xs">{v.name}</span>
                                <span className="font-zahl text-[9.5px] opacity-75">
                                  {v.hinweis}
                                </span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      <label className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        Welche Schichten
                      </label>
                      <div className="flex flex-col gap-1.5">
                        {schichtarten.map((t) => {
                          const g = gewaehlt[t.code] ?? { an: false, von: '04:00', bis: '14:45' };
                          return (
                            <div
                              key={t.code}
                              className={`flex flex-wrap items-center gap-2 rounded-[4px] border px-2 py-1.5 ${
                                g.an ? 'border-akzent bg-akzent-tief' : 'border-linie bg-flaeche'
                              }`}
                            >
                              <button
                                onClick={() =>
                                  setGewaehlt((v) => ({ ...v, [t.code]: { ...g, an: !g.an } }))
                                }
                                aria-pressed={g.an}
                                className={`min-w-[62px] rounded-[4px] border px-[11px] py-1 font-zahl text-xs ${
                                  g.an
                                    ? 'border-akzent bg-akzent text-akzent-satz'
                                    : 'border-linie-2 bg-flaeche text-tinte-3'
                                }`}
                              >
                                {SCHICHT_LABEL[t.code] ?? t.label ?? t.name ?? t.code}
                              </button>
                              {g.an ? (
                                <>
                                  <input
                                    type="time"
                                    value={g.von}
                                    step={900}
                                    onChange={(e) => setzeZeit(t.code, 'von', e.target.value)}
                                    className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-sm text-tinte"
                                  />
                                  <span className="text-tinte-3">–</span>
                                  <input
                                    type="time"
                                    value={g.bis}
                                    step={900}
                                    onChange={(e) => setzeZeit(t.code, 'bis', e.target.value)}
                                    className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-sm text-tinte"
                                  />
                                  {g.bis < g.von && (
                                    <span className="font-zahl text-[10px] text-stand">
                                      über Mitternacht
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="font-zahl text-[10px] text-tinte-3">aus</span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Die Grenze zwischen zwei Schichten ist EINE Grenze. Eine
                      Überlappung macht Erfassungen mehrdeutig und wird gesperrt;
                      eine Lücke kann gewollt sein und wird nur benannt. */}
                      {anschluss.ueberlappungen.map((f) => (
                        <p key={`u-${f.von}`} className="text-[13px] text-stoer">
                          <b>
                            {SCHICHT_LABEL[f.von] ?? f.von} und {SCHICHT_LABEL[f.nach] ?? f.nach}{' '}
                            überlappen sich
                          </b>
                          : zwischen {f.bis} und {f.ab} laufen beide. Eine Erfassung dort gehörte zu
                          beiden Schichten.
                        </p>
                      ))}
                      {anschluss.luecken.map((f) => (
                        <p key={`l-${f.von}`} className="text-[13px] text-stand">
                          <b>
                            Lücke zwischen {SCHICHT_LABEL[f.von] ?? f.von} und{' '}
                            {SCHICHT_LABEL[f.nach] ?? f.nach}
                          </b>
                          : von {f.ab} bis {f.bis} ist keine Schicht geplant. Was dann erfasst wird,
                          gehört zu keiner.
                        </p>
                      ))}
                      {anschluss.ueberlappungen.length === 0 &&
                        anschluss.luecken.length === 0 &&
                        aktiveArten.length > 1 && (
                          <p className="text-[13px] text-lauf">
                            Die Schichten schließen nahtlos aneinander an.
                          </p>
                        )}

                      {bestehende > 0 && (
                        <p className="text-[13px] text-tinte-2">
                          {bestehende === 1
                            ? 'Eine Schicht besteht'
                            : `${bestehende} Schichten bestehen`}{' '}
                          schon — <b>dort werden die Zeiten geändert</b>.
                        </p>
                      )}

                      {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                      {bilanz && (
                        <div className="rounded-[4px] border border-linie bg-flaeche-2 px-3 py-2 text-[13px] text-tinte-2">
                          {bilanz.angelegt > 0 && <div>{bilanz.angelegt} Schichten angelegt</div>}
                          {bilanz.geaendert > 0 && <div>{bilanz.geaendert} Schichten geändert</div>}
                          {bilanz.uebersprungen.map((u) => (
                            <div key={`${u.tag}-${u.art}`} className="text-stand">
                              {u.tag.split('-').reverse().join('.')} {SCHICHT_LABEL[u.art] ?? u.art}
                              : {u.grund}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <button
                          disabled={
                            sende || !aktiveArten.length || anschluss.ueberlappungen.length > 0
                          }
                          onClick={() => void plane()}
                          className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                        >
                          {anschluss.ueberlappungen.length > 0
                            ? 'Überlappung zuerst auflösen'
                            : knopfText}
                        </button>
                        <button
                          disabled={sende}
                          onClick={() => setAuswahl(null)}
                          className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                        >
                          {bilanz ? 'Schließen' : 'Abbrechen'}
                        </button>
                      </div>
                    </div>
                  </Karte>
                ) : (
                  <Karte
                    titel={`${SCHICHT_LABEL[auswahl.instanz.shiftTypeCode] ?? auswahl.instanz.shiftTypeCode} am ${auswahl.instanz.datum.split('-').reverse().join('.')}`}
                    marke={auswahl.instanz.herkunft === 'IMPORT' ? 'Aus dem Altbestand' : undefined}
                  >
                    <div className="flex flex-col gap-3">
                      {laeuft(auswahl.instanz) && (
                        <p className="text-[13px] text-tinte-2">
                          Diese Schicht läuft gerade — nur das <b>Ende</b> kann vorgezogen werden,
                          frühestens auf die aktuelle Uhrzeit.
                        </p>
                      )}
                      {auswahl.instanz.status !== 'AUSGEFALLEN' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                            Von
                          </label>
                          <input
                            type="time"
                            value={formStart}
                            onChange={(e) => setFormStart(e.target.value)}
                            step={900}
                            disabled={laeuft(auswahl.instanz)}
                            className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1.5 font-zahl text-sm text-tinte disabled:opacity-50"
                          />
                          <label className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                            Bis
                          </label>
                          <input
                            type="time"
                            value={formEnde}
                            onChange={(e) => setFormEnde(e.target.value)}
                            step={900}
                            className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1.5 font-zahl text-sm text-tinte"
                          />
                        </div>
                      )}

                      {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}

                      <div className="flex flex-wrap gap-2">
                        {auswahl.instanz.status === 'AUSGEFALLEN' ? (
                          <button
                            disabled={sende}
                            onClick={() =>
                              void aktion(
                                `/api/shifts/instanzen/${auswahl.instanz.id}/einplanen`,
                                'POST',
                              )
                            }
                            className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                          >
                            Wieder einplanen
                          </button>
                        ) : (
                          <>
                            <button
                              disabled={sende || !formStart || !formEnde}
                              onClick={() =>
                                void aktion(
                                  `/api/shifts/instanzen/${auswahl.instanz.id}`,
                                  'PATCH',
                                  { startTime: formStart, endTime: formEnde },
                                )
                              }
                              className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                            >
                              Zeiten speichern
                            </button>
                            {!laeuft(auswahl.instanz) && (
                              <button
                                disabled={sende}
                                onClick={() =>
                                  void aktion(
                                    `/api/shifts/instanzen/${auswahl.instanz.id}/streichen`,
                                    'POST',
                                  )
                                }
                                className="rounded-[4px] border border-stoer bg-flaeche px-3 py-1.5 font-zahl text-xs text-stoer disabled:opacity-50"
                              >
                                Schicht fällt aus
                              </button>
                            )}
                          </>
                        )}
                        <button
                          disabled={sende}
                          onClick={() => setAuswahl(null)}
                          className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  </Karte>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Geplante Schichten"
              wert={String(kuenftige)}
              neben="nächste 8 Wochen"
            />
            <Kennzahl
              marke="Schichtarten"
              klein
              wert={
                [...new Set(instanzen.map((i) => i.shiftTypeCode))]
                  .map((t) => SCHICHT_LABEL[t] ?? t)
                  .join(' · ') || '—'
              }
            />
            <Kennzahl
              marke="Geplant bis"
              wert={horizont.letzterTag ? horizont.letzterTag.split('-').reverse().join('.') : '—'}
              tendenz={horizont.knapp ? 'runter' : 'hoch'}
              neben={`${horizont.tageVoraus} Tage voraus`}
            />
          </div>

          <Karte titel="Schichtplan" marke="2 Wochen zurück · 8 Wochen voraus" tabelle>
            <div className="min-w-[720px]">
              <div className="grid grid-cols-[52px_repeat(7,1fr)] gap-px">
                <div />
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((t) => (
                  <div
                    key={t}
                    className="pb-2 text-center font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3"
                  >
                    {t}
                  </div>
                ))}
                {wochen.map((tage, wIdx) => [
                  <div
                    key={`kw-${tage[0]}`}
                    className="flex flex-col items-end gap-1 pr-2 pt-1.5 font-zahl text-[10px] text-tinte-3"
                  >
                    <span>KW {kw(tage[0])}</span>
                    {/* Eine leere Zukunftswoche bietet an, die letzte geplante
                          zu übernehmen — der häufigste Fall ist „nächste Woche
                          wie diese". */}
                    {uebernahmeQuelle(tage) && (
                      <button
                        onClick={() => void uebernimmWoche(tage, uebernahmeQuelle(tage)!)}
                        disabled={sende}
                        title={`Alle Schichten der KW ${kw(uebernahmeQuelle(tage)![0])} auf diese Woche übertragen`}
                        className="rounded-[4px] border border-linie-2 bg-flaeche px-1 py-0.5 text-right font-zahl text-[9px] leading-tight text-tinte-3 hover:border-akzent hover:text-akzent disabled:opacity-50"
                      >
                        wie
                        <br />
                        KW {kw(uebernahmeQuelle(tage)![0])}
                      </button>
                    )}
                  </div>,
                  ...tage.map((tag, sIdx) => {
                    const heutig = tag === heute;
                    const vergangen = tag < heute;
                    const liste = jeTag.get(tag) ?? [];
                    const markiert = imZug(wIdx, sIdx);
                    return (
                      <div
                        key={tag}
                        onMouseDown={
                          vergangen
                            ? undefined
                            : (e) => {
                                e.preventDefault();
                                setZiehen({ vonW: wIdx, vonS: sIdx, bisW: wIdx, bisS: sIdx });
                              }
                        }
                        onMouseEnter={
                          ziehen
                            ? () => setZiehen((z) => (z ? { ...z, bisW: wIdx, bisS: sIdx } : z))
                            : undefined
                        }
                        role={vergangen ? undefined : 'button'}
                        tabIndex={vergangen ? undefined : 0}
                        onKeyDown={
                          vergangen
                            ? undefined
                            : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  oeffnePlanen([tag]);
                                }
                              }
                        }
                        title={
                          vergangen
                            ? undefined
                            : 'Klicken oder über mehrere Tage ziehen, um zu planen'
                        }
                        className={`min-h-[64px] select-none rounded-[4px] border p-1.5 ${
                          markiert
                            ? 'border-akzent bg-akzent-tief ring-1 ring-inset ring-akzent'
                            : heutig
                              ? 'border-akzent bg-akzent-tief'
                              : 'border-linie bg-flaeche'
                        } ${vergangen ? 'opacity-55' : 'cursor-pointer hover:border-akzent focus:outline-none focus:ring-1 focus:ring-akzent'}`}
                      >
                        <div className="mb-1 flex items-center justify-between font-zahl text-[10px] text-tinte-3">
                          <span>
                            {tag.slice(8, 10)}.{tag.slice(5, 7)}.
                          </span>
                          {!vergangen && (
                            <span aria-hidden className="leading-none text-tinte-3">
                              +
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          {liste.map((i) => (
                            <button
                              key={i.id}
                              title={i.herkunft === 'IMPORT' ? 'Aus dem Altbestand übernommen' : ''}
                              onClick={(e) => {
                                e.stopPropagation();
                                waehleBearbeiten(i);
                              }}
                              disabled={i.status === 'GESCHLOSSEN' || istVorbei(i)}
                              className={`inline-flex items-center gap-1 whitespace-nowrap text-left ${
                                i.status === 'GESCHLOSSEN' || istVorbei(i)
                                  ? 'cursor-default'
                                  : 'cursor-pointer hover:opacity-80'
                              } ${i.status === 'AUSGEFALLEN' ? 'opacity-50' : ''}`}
                            >
                              <SchichtChip code={i.shiftTypeCode} farbe={i.farbe} />
                              <span
                                className={`font-zahl text-[10px] text-tinte-3 ${i.status === 'AUSGEFALLEN' ? 'line-through' : ''}`}
                              >
                                {i.status === 'AUSGEFALLEN'
                                  ? 'fällt aus'
                                  : `${i.startTime}–${i.endTime}`}
                              </span>
                              {i.bearbeitet && i.status !== 'AUSGEFALLEN' && (
                                <span
                                  className="text-[9px] text-akzent"
                                  title="abweichend vom Muster"
                                >
                                  ●
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }),
                ])}
              </div>
            </div>
          </Karte>

          <p className="border-t border-linie pt-4 text-xs text-tinte-3">
            Über mehrere Tage ziehen, um sie zusammen zu planen — die passende Vorlage ist dabei
            schon gewählt. Auf eine vorhandene Schicht klicken, um sie zu ändern oder zu streichen.
            Eine leere Woche übernimmt per „wie KW nn" die letzte geplante. Vergangene Tage sind
            gedimmt, heute ist markiert.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
